import { ReactElement, useState } from 'react'
import { Box } from '@mui/material'
import { UseMutationResult } from '@tanstack/react-query'
import { CreateDialog, EditAssemblyDialog } from 'src/components/manager/assemblies'
import AssemblyTypeOverview from './assemblyTypeOverview'

interface Props {
  assemblies: Assembly[]
  assignments: AssemblyAssignment[]
  onSetStandard: (assembly: Assembly) => void
  isSettingStandard: boolean
  onDelete: (assembly: Assembly) => Promise<Assembly[]>
  onDownload: (assembly: Assembly) => Promise<void>
  onStore: (assembly: Assembly) => Promise<CLTAssembly | TimberFrameAssembly>
  createMutation: UseMutationResult<CLTAssembly | TimberFrameAssembly, unknown, Assembly, unknown>
  editMutation: UseMutationResult<CLTAssembly | TimberFrameAssembly, unknown, Assembly, unknown>

  earthMassForce: number
}

const AssemblySelection = ({
  assemblies,
  assignments,
  onSetStandard,
  isSettingStandard,
  onDelete,
  onDownload,
  onStore,
  createMutation,
  editMutation,
  earthMassForce,
}: Props): ReactElement => {
  const [createValues, setCreateValues] = useState<Assembly | null>(null)
  const [createAllowedUsageTypes, setCreateAllowedUsageTypes] = useState<
    AssemblyTypes[] | undefined
  >(undefined)
  const [editValues, setEditValues] = useState<Assembly | null>(null)

  const handleSetCreateValues = (assembly: Assembly, allowedUsageTypes?: AssemblyTypes[]) => {
    setCreateValues(assembly)
    setCreateAllowedUsageTypes(allowedUsageTypes)
  }

  return (
    <>
      <Box>
        <AssemblyTypeOverview
          key={'Inner'}
          assemblies={assemblies}
          assignments={assignments}
          assemblyType={'Inner'}
          onDelete={onDelete}
          onSetStandard={onSetStandard}
          isSettingStandard={isSettingStandard}
          onDownload={onDownload}
          onStore={onStore}
          setCreateValues={handleSetCreateValues}
          setEditValues={setEditValues}
          editMutation={editMutation}
          earthMassForce={earthMassForce}
        />
        <AssemblyTypeOverview
          key={'Outer'}
          assemblies={assemblies}
          assignments={assignments}
          assemblyType={'Outer'}
          onDelete={onDelete}
          onSetStandard={onSetStandard}
          isSettingStandard={isSettingStandard}
          onDownload={onDownload}
          onStore={onStore}
          setCreateValues={handleSetCreateValues}
          setEditValues={setEditValues}
          editMutation={editMutation}
          earthMassForce={earthMassForce}
        />
        <AssemblyTypeOverview
          key={'Ceiling'}
          assemblies={assemblies}
          assignments={assignments}
          assemblyType={'Ceiling'}
          onDelete={onDelete}
          onSetStandard={onSetStandard}
          isSettingStandard={isSettingStandard}
          onDownload={onDownload}
          onStore={onStore}
          setCreateValues={handleSetCreateValues}
          setEditValues={setEditValues}
          editMutation={editMutation}
          earthMassForce={earthMassForce}
        />
        <AssemblyTypeOverview
          key={'Roof'}
          assemblies={assemblies}
          assignments={assignments}
          assemblyType={'Roof'}
          onDelete={onDelete}
          onSetStandard={onSetStandard}
          isSettingStandard={isSettingStandard}
          onDownload={onDownload}
          onStore={onStore}
          setCreateValues={handleSetCreateValues}
          setEditValues={setEditValues}
          editMutation={editMutation}
          earthMassForce={earthMassForce}
        />
      </Box>
      {createValues && (
        <CreateDialog
          mutation={createMutation}
          open
          onClose={() => {
            setCreateValues(null)
            setCreateAllowedUsageTypes(undefined)
          }}
          assembly={createValues}
          earthMassForce={earthMassForce}
          allowedUsageTypes={createAllowedUsageTypes}
        />
      )}
      {editValues && (
        <EditAssemblyDialog
          mutation={editMutation}
          open
          onClose={() => setEditValues(null)}
          assembly={editValues}
          earthMassForce={earthMassForce}
        />
      )}
    </>
  )
}

export default AssemblySelection
